import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { StarBurst } from "../ui/StarBurst";
import type { SentenceBuilderQuestion } from "../levelTypes";

interface Props {
  question: SentenceBuilderQuestion;
  onAnswer: (result: "correct" | "wrong") => void;
}

type Phase = "idle" | "correct" | "wrong";

function shuffleArray<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function SentenceBuilder({ question, onAnswer }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  // Ordered list of word indices currently placed in the answer row
  const [placed, setPlaced] = useState<number[]>([]);

  // Shuffle once per question
  const shuffled = useMemo(
    () => shuffleArray(question.words.map((w, i) => ({ word: w, idx: i }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question.words.join("|")]
  );

  const usedSet = new Set(placed);

  function addWord(idx: number) {
    if (phase !== "idle" || usedSet.has(idx)) return;
    setPlaced((prev) => [...prev, idx]);
  }

  function removeWord(placedPos: number) {
    if (phase !== "idle") return;
    setPlaced((prev) => prev.filter((_, i) => i !== placedPos));
  }

  function checkAnswer() {
    if (phase !== "idle" || placed.length !== question.words.length) return;
    const isCorrect = placed.every(
      (idx, pos) => question.words[idx] === question.words[pos]
    );
    setPhase(isCorrect ? "correct" : "wrong");
    setTimeout(() => {
      onAnswer(isCorrect ? "correct" : "wrong");
    }, 1600);
  }

  const answerRowClass =
    phase === "correct"
      ? "sb__answer-row sb__answer-row--correct"
      : phase === "wrong"
      ? "sb__answer-row sb__answer-row--wrong"
      : "sb__answer-row";

  return (
    <div className="exercise-card">
      <StarBurst active={phase === "correct"} />

      <p className="sb__context-label">Sett ordene i riktig rekkefølge</p>
      <h2 className="sb__translation">{question.translation}</h2>

      {/* Answer slots */}
      <motion.div
        className={answerRowClass}
        animate={
          phase === "wrong"
            ? { x: [0, -10, 10, -8, 8, -4, 4, 0] }
            : { x: 0 }
        }
        transition={{ duration: 0.4 }}
      >
        {placed.length === 0 ? (
          <span className="sb__answer-empty">Klikk ord nedenfor…</span>
        ) : (
          <AnimatePresence mode="popLayout">
            {placed.map((wordIdx, pos) => (
              <motion.button
                key={`${wordIdx}-${pos}`}
                className="word-chip word-chip--placed"
                layout
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.7, opacity: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 25 }}
                onClick={() => removeWord(pos)}
                disabled={phase !== "idle"}
                aria-label={`Fjern ${question.words[wordIdx]}`}
              >
                {question.words[wordIdx]}
              </motion.button>
            ))}
          </AnimatePresence>
        )}
      </motion.div>

      {/* Word pool */}
      <div className="sb__pool">
        {shuffled.map(({ word, idx }) => (
          <motion.button
            key={idx}
            className={`word-chip word-chip--pool${usedSet.has(idx) ? " used" : ""}`}
            onClick={() => addWord(idx)}
            disabled={phase !== "idle" || usedSet.has(idx)}
            whileTap={!usedSet.has(idx) ? { scale: 0.93 } : {}}
          >
            {word}
          </motion.button>
        ))}
      </div>

      {/* Check button */}
      <button
        className="sb__check"
        onClick={checkAnswer}
        disabled={placed.length !== question.words.length || phase !== "idle"}
      >
        Sjekk svar
      </button>

      {/* Wrong: show correct answer */}
      {phase === "wrong" && (
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: 12, color: "#9c7a4a", fontSize: 13 }}
        >
          Riktig:{" "}
          <strong style={{ color: "#f2ead8" }}>{question.words.join(" ")}</strong>
        </motion.p>
      )}
    </div>
  );
}
